import { useEffect, useRef, useState } from 'react';
import Dialog, { Props as DialogProps } from '../Dialog/Dialog';
import { SubCategoryFilter, TimeFilter, translate } from '@micro-apps/env';
import { CTA, Switch } from '..';

type Props = {
	dialog: any;
	timeFilters?: TimeFilter[];
	subCategoryFilters?: SubCategoryFilter[];
	complimentaryFilter?: boolean;
	results?: number;
	onChange?: ({
		timeFilters,
		subCategoryFilters,
		complimentaryFilter,
	}: {
		timeFilters: TimeFilter[];
		subCategoryFilters: SubCategoryFilter[];
		complimentaryFilter: boolean;
	}) => void;
} & DialogProps;

const FilterDialog = ({
	dialog,
	timeFilters,
	subCategoryFilters,
	complimentaryFilter = false,
	onChange,
	results = 0,
	...rest
}: Props) => {
	const [viewMoreExpanded, setViewMoreExpanded] = useState(false);
	const checkboxes = useRef<HTMLInputElement[]>([]);
	const [timeFilterCheckedFilters, setTimeFilterCheckedFilters] = useState<TimeFilter[]>([]);
	const [subCategoryFilterCheckedFilters, setSubCategoryFilterCheckedFilters] = useState<
		SubCategoryFilter[]
	>([]);
	const [isComplimentaryItemsOnly, setIsComplimentaryItemsOnly] = useState(complimentaryFilter);

	const handleTimeFilterChange = (index: number) => {
		const updatedCheckedFilters = [...timeFilterCheckedFilters];
		const timeFilter = timeFilters?.[index];

		const currIndex = timeFilterCheckedFilters.findIndex(
			(filter: TimeFilter) => filter.label === timeFilter?.label
		);
		if (timeFilter && currIndex > -1) {
			updatedCheckedFilters.splice(currIndex, 1);
		} else if (timeFilter) {
			updatedCheckedFilters.push(timeFilter);
		}

		setTimeFilterCheckedFilters(updatedCheckedFilters);
	};
	const handleSubCategoryFilterChange = (index: number) => {
		const updatedCheckedFilters = [...subCategoryFilterCheckedFilters];
		const subCategoryFilter = subCategoryFilters?.[index];
		const currIndex = subCategoryFilterCheckedFilters.findIndex(
			(filter: SubCategoryFilter) => filter.id === subCategoryFilter?.id
		);
		if (subCategoryFilter && currIndex > -1) {
			updatedCheckedFilters.splice(currIndex, 1);
		} else if (subCategoryFilter) {
			updatedCheckedFilters.push(subCategoryFilter);
		}
		setSubCategoryFilterCheckedFilters(updatedCheckedFilters);
	};
	const handleComplimentaryFilterChange = () => {
		const newValue = !isComplimentaryItemsOnly;
		setIsComplimentaryItemsOnly(newValue);
	};
	const handleClearFilters = () => {
		setTimeFilterCheckedFilters([]);
		setSubCategoryFilterCheckedFilters([]);
		setIsComplimentaryItemsOnly(false);
		checkboxes.current.forEach(checkbox => {
			checkbox.checked = false;
		});
	};

	useEffect(() => {
		onChange?.({
			timeFilters: timeFilterCheckedFilters,
			subCategoryFilters: subCategoryFilterCheckedFilters,
			complimentaryFilter: isComplimentaryItemsOnly,
		});
	}, [
		timeFilterCheckedFilters,
		onChange,
		subCategoryFilterCheckedFilters,
		isComplimentaryItemsOnly,
	]);

	return (
		<Dialog
			{...rest}
			dialogRef={instance => {
				dialog.current = instance;
			}}
			className="Warningmodal-content filter-modal"
			classNames={{
				container: 'Dialog hidden',
				overlay: 'Dialog-overlay',
				dialog: 'Dialog-wrapper filter-modal',
				title: 'Dialog-title title',
				closeButton: 'Dialog-closebutton',
			}}
			maxWrapperHeightToContent
			disableScroll
		>
			<div className="WideGridFilter-modal surface-overlay">
				<div className="Heading WideGridFilter-modal-heading">
					<h1 className="Heading-title WideGridFilter-modal-heading-title labels-label">
						{translate('all filters')}
					</h1>
				</div>
				<div className="WideGridFilter-modal-body">
					<div className="WideGridFilter-modal-sections-container">
						<div className="WideGridFilter-modal-section">
							{timeFilters?.length ? (
								<div className="WideGridFilter-modal-section-itemContainer">
									<div className="Heading WideGridFilter-modal-section-heading">
										<h2 className="Heading-title WideGridFilter-modal-section-title subtitle-2">
											{translate('Time Of Day')}
										</h2>
									</div>
									{timeFilters?.map((timeFilter, i) => {
										const id = `filter-${timeFilter.label.replace(/\s+/g, '-').toLowerCase()}`;
										return (
											<div
												key={timeFilter.label + i}
												className="Checkbox formElement Checkbox--light checkbox-token WideGridFilter-filter-checkbox"
											>
												<input
													className="Checkbox-input formElement-field"
													type="checkbox"
													name={id}
													id={id}
													onChange={() => handleTimeFilterChange(i)}
													ref={el => {
														if (el && !checkboxes.current.find(item => item === el)) {
															checkboxes.current.push(el);
														}
													}}
												/>
												<label
													className="Checkbox-decoration"
													aria-hidden="true"
													htmlFor={id}
													role="presentation"
												></label>
												<label
													htmlFor={id}
													className="Checkbox-label WideGridFilter-filter-checkbox-label labels-caption"
												>
													{timeFilter.label}
												</label>
											</div>
										);
									})}
								</div>
							) : null}
							{subCategoryFilters?.length ? (
								<div className="WideGridFilter-modal-section-itemContainer">
									<div className="Heading WideGridFilter-modal-section-heading">
										<h2 className="Heading-title WideGridFilter-modal-section-title subtitle-2">
											{translate('Activities')}
										</h2>
									</div>
									{subCategoryFilters?.map((subCategoryFilter, i) => {
										return i < 6 || viewMoreExpanded ? (
											<div
												key={subCategoryFilter.id + i}
												className="Checkbox formElement Checkbox--light checkbox-token WideGridFilter-filter-checkbox"
											>
												<input
													className="Checkbox-input formElement-field"
													type="checkbox"
													name={subCategoryFilter.id}
													id={subCategoryFilter.id}
													onChange={() => handleSubCategoryFilterChange(i)}
													ref={el => {
														if (el && !checkboxes.current.find(item => item === el)) {
															checkboxes.current.push(el);
														}
													}}
												/>
												<label
													className="Checkbox-decoration"
													aria-hidden="true"
													htmlFor={subCategoryFilter.id}
													role="presentation"
												></label>
												<label
													htmlFor={subCategoryFilter.id}
													className="Checkbox-label WideGridFilter-filter-checkbox-label labels-caption"
												>
													{subCategoryFilter.name}
												</label>
											</div>
										) : null;
									})}
									{subCategoryFilters.length > 6 ? (
										<div className="">
											<CTA onClick={() => setViewMoreExpanded(!viewMoreExpanded)} type="underlined">
												{viewMoreExpanded ? translate('View Less') : translate('View More')}
											</CTA>
										</div>
									) : null}
								</div>
							) : null}
							{/* Pricing */}
							<div className="WideGridFilter-modal-section-itemContainer WideGridFilter-modal-section-pricing">
								<div className="Heading WideGridFilter-modal-section-heading">
									<h2 className="Heading-title WideGridFilter-modal-section-title subtitle-2">
										{translate('Pricing')}
									</h2>
								</div>
								<div className="Complimentary-items-only">
									<label htmlFor="complimentary-only" className="labels-caption">
										{translate('Complimentary items only')}
									</label>
									<Switch
										theme="light"
										checked={isComplimentaryItemsOnly}
										onChange={handleComplimentaryFilterChange}
										className="Switch-complimentary"
									/>
								</div>
							</div>
							{/* Footer */}
							<div className="WideGridFilter-modal-footer">
								<button onClick={handleClearFilters} className="button-text">
									<span className="Button-label">{translate('Clear')}</span>
								</button>{' '}
								<button
									onClick={() => dialog.current?.hide() && rest?.onHide?.()}
									className="button-filled"
								>
									<span className="Button-label">
										{translate('Show {0} Results', [results.toString()])}
									</span>
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Dialog>
	);
};

export default FilterDialog;
